import { useEffect, useState } from 'react';
import { useLocalWorkspaceServerStatus } from '@/hooks/use-local-workspace-server-status';
import { errorMessage } from '@/lib/error-message';
import type { LocalWorkspaceServerStatus } from '@/lib/local-runtime';
import { createLocalWorkspaceClient } from '@/lib/workspaces/local-workspace-client';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/**
 * Owns the frame-based workspace client bound to a locally-spawned CLI server for
 * the given `workspaceRoot`: creates one when a root is provided and Electron
 * exposed a local server, waits for its WebSocket handshake, and tears it down
 * (closing the socket) on change or unmount. Mirrors {@link useCloudWorkspaceClient}
 * so the side panel gets terminals + fs + git over the same frame protocol the
 * cloud uses. Returns `null` while the mandatory local server is starting or
 * after a terminal startup failure.
 */
export function useLocalWorkspaceClient(
  workspaceRoot: string | null,
  onError: (message: string) => void,
): { client: WorkspaceClient | null; status: LocalWorkspaceServerStatus | null } {
  const [localClient, setLocalClient] = useState<WorkspaceClient | null>(null);
  const serverStatus = useLocalWorkspaceServerStatus();
  useEffect(() => {
    const server = serverStatus?.state === 'ready' ? serverStatus.server : null;
    if (workspaceRoot === null || server === null) {
      setLocalClient(null);
      if (serverStatus?.state === 'failed') onError(serverStatus.message);
      return;
    }
    const client = createLocalWorkspaceClient(server, workspaceRoot);
    let disposed = false;
    void client.ready.then(
      () => {
        if (!disposed) setLocalClient(client);
      },
      (error: unknown) => {
        if (!disposed) onError(errorMessage(error));
      },
    );
    return () => {
      disposed = true;
      setLocalClient((current) => (current === client ? null : current));
      client.close();
    };
  }, [workspaceRoot, onError, serverStatus]);
  return { client: localClient, status: serverStatus };
}
